// Default Libraries
import { createActions, asyncAction } from '../helpers/redux';
import { push } from 'react-router-redux';
import { schema, normalize } from 'normalizr';
import Immutable from 'immutable';

const relationshipTypeSchema = new schema.Entity('relationshipTypes');

export const RelationshipTypeActions = createActions({
  @asyncAction()
  async getProjectRelationshipTypes(projectId) {
    return ({ client }) => {
      return client
        .get(`/project/${projectId}/relationship_types`)
        .then(response => {
          return normalize(response.relationshipTypes, [
            relationshipTypeSchema,
          ]);
        });
    };
  },

  @asyncAction()
  async renameRelationshipType(relationshipTypeId, typeName) {
    return ({ client }) => {
      return client
        .patch(`/relationship_types/${relationshipTypeId}`, {
          body: { type: typeName },
        })
        .then(response => {
          return normalize(response, relationshipTypeSchema);
        });
    };
  },

  @asyncAction()
  async getRelationshipType(relationshipTypeId) {
    return ({ client }) => {
      return client
        .get(`/relationship_types/${relationshipTypeId}`)
        .then(response => {
          return normalize(response, relationshipTypeSchema);
        });
    };
  },

  @asyncAction()
  async createRelationshipType(relationshipType) {
    return ({ client }) => {
      return client
        .post(`/relationship_types`, {
          body: relationshipType,
        })
        .then(response => {
          return normalize(response, relationshipTypeSchema);
        });
    };
  },

  @asyncAction()
  async saveDescription(relationshipTypeId, description) {
    return ({ client }) => {
      return client
        .patch(`/relationship_types/${relationshipTypeId}`, {
          body: { description: description },
        })
        .then(response => {
          return normalize(response, relationshipTypeSchema);
        });
    };
  },

  @asyncAction()
  async createRelationshipTypeProperty(relationshipType, props) {
    return async ({ client, getState, Checkit }) => {
      const relationshipTypeId = relationshipType.get('id');
      const propName = props.propName;
      const defaultMeta = props.defaultMeta;
      const existingProps = relationshipType.get('props');

      const checkit = new Checkit({
        propertyName: [
          'required',
          function(val) {
            if (existingProps.has(val))
              throw new Error('A property with the same name already exist.');
            return true;
          },
        ],
      });

      try {
        await checkit.run({ propertyName: propName });
        let propData = existingProps.set(
          propName,
          Immutable.fromJS(defaultMeta),
        );

        propData = propData.map((prop, key) => {
          if (key !== propName) {
            let order = parseInt(prop.get('_order'), 10);
            return prop.set('_order', order + 1);
          } else {
            return prop;
          }
        });

        const newProps = {
          props: propData.toJS(),
        };
        return client
          .patch(`/relationship_types/${relationshipTypeId}`, {
            body: newProps,
          })
          .then(response => {
            return response;
          });
      } catch (err) {
        let error;
        if (err instanceof Checkit.Error) {
          error = err.toJSON();
        } else {
          error = err.json.errors;
        }
        throw error;
      }
    };
  },

  @asyncAction()
  async deleteRelationshipType(relationshipType) {
    return async ({ dispatch, client, getState, Checkit }) => {
      return client
        .delete(`/relationship_types/${relationshipType.relationshipTypeId}`)
        .then(response => {
          dispatch(push(`/project/${relationshipType.currentProjectId}`));
          //response['relationshipTypeId'] = relationshipType.relationshipTypeId;
          return { relationshipTypeId: relationshipType.relationshipTypeId };
        });

      // .then((a) => {
      //   dispatch(pushState(null, `/project/${relationshipType.projectId}/default`));
      // });
      // return client.delete(`/relationship_types/${relationshipType.relationshipTypeId}/props/${relationshipType.prop}`).then((response) => {
      //   let rsp = response;
      //   rsp.relationshiptype_id = relationshipType.relationshipTypeId;
      //   rsp.deleted_key = relationshipType.prop;
      //   return rsp;
      // });
    };
  },

  @asyncAction()
  async deleteRelationshipTypeProperty(relationshipType) {
    return async ({ client, getState, Checkit }) => {
      return client
        .delete(
          `/relationship_types/${relationshipType.relationshipTypeId}/props/${relationshipType.prop}`,
        )
        .then(response => {
          let rsp = response;
          rsp.relationshiptype_id = relationshipType.relationshipTypeId;
          rsp.deleted_key = relationshipType.prop;
          return rsp;
        });
    };
  },

  @asyncAction()
  async renameRelationshipTypeProperty(relationshipType) {
    return async ({ client, getState, Checkit }) => {
      return client
        .patch(
          `/relationship_types/${relationshipType.relationshipTypeId}/props/${relationshipType.prop}/rename`,
          {
            body: relationshipType,
          },
        )
        .then(response => {
          let rsp = response;
          rsp.relationshiptype_id = relationshipType.relationshipTypeId;
          rsp.deleted_key = relationshipType.prop;
          rsp.new_key = relationshipType.newPropKey;
          return rsp;
        });
    };
  },

  @asyncAction()
  async saveRelationshipTypeProperty(relationshipType, propKey, meta) {
    return async ({ client, getState, Checkit }) => {
      const relationshipTypeId = relationshipType.get('id');

      return client
        .patch(`/relationship_types/${relationshipTypeId}/props/${propKey}`, {
          body: meta,
        })
        .then(response => {
          return response;
        });
    };
  },

  @asyncAction()
  async createRelationshipTypeMetaProperty(obj) {
    return async ({ client, getState, Checkit }) => {
      return client
        .post(
          `/relationship_types/${obj.relationshipTypeId}/props/${obj.prop}`,
          {
            body: obj,
          },
        )
        .then(response => {
          let rsp = response;
          rsp.relationshiptype_id = obj.relationshipTypeId;
          rsp.prop = obj.prop;
          rsp.meta_key = obj.metaKey;
          rsp.meta_value = obj.metaValue;
          return rsp;
        });
    };
  },

  @asyncAction()
  async renameRelationshipTypeMetaProperty(obj) {
    return async ({ client, getState, Checkit }) => {
      return client
        .patch(
          `/relationship_types/${obj.relationshipTypeId}/props/${obj.prop}/${obj.metaKey}/rename`,
          {
            body: obj,
          },
        )
        .then(response => {
          let rsp = {};
          rsp.relationshiptype_id = obj.relationshipTypeId;
          rsp.prop = obj.prop;
          rsp.deleted_meta_key = obj.metaKey;
          rsp.new_meta_key = obj.newMetaKey;
          return rsp;
        });
    };
  },

  @asyncAction()
  async deleteRelationshipTypeMetaProperty(obj) {
    return async ({ client, getState, Checkit }) => {
      return client
        .delete(
          `/relationship_types/${obj.relationshipTypeId}/props/${obj.prop}/${obj.metaKey}`,
        )
        .then(response => {
          let rsp = response;
          rsp.relationshiptype_id = obj.relationshipTypeId;
          rsp.prop = obj.prop;
          rsp.meta_key = obj.metaKey;
          return rsp;
        });
    };
  },

  @asyncAction()
  async updateRelationshipTypeMetaValue(obj) {
    return async ({ client, getState, Checkit }) => {
      return client
        .patch(
          `/relationship_types/${obj.relationshipTypeId}/props/${obj.prop}/${obj.metaKey}`,
          {
            body: obj,
          },
        )
        .then(response => {
          return response;
        });
    };
  },

  updatePropsOrder(relationshipTypeId, dragKey, hoverKey) {
    return {
      relationshipTypeId,
      dragKey,
      hoverKey,
    };
  },

  @asyncAction()
  async updateRelationshipTypeProperties(relationshipType) {
    return async ({ client, getState, Checkit }) => {
      const relationshipTypeId = relationshipType.get('id');

      try {
        return client
          .put(`/relationship_types/${relationshipTypeId}/props`, {
            body: relationshipType.get('props'),
          })
          .then(response => {
            return response;
          });
      } catch (err) {
        let error;
        if (err instanceof Checkit.Error) {
          error = err.toJSON();
        } else {
          error = err.json.errors;
        }
        throw error;
      }
    };
  },
});



// WEBPACK FOOTER //
// ./src/actions/relationshipType.js